import { isEmpty, map, size } from 'lodash'
import moment from 'moment-timezone'
import React, { useCallback } from 'react'
import { useEffectOnce } from 'react-use'
import { RouteComponentProps } from 'react-router'
import {
  TableInstance,
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  UseRowSelectInstanceProps,
  useSortBy,
  useTable,
} from 'react-table'
import AddIcon from '@material-ui/icons/Add'
import CancelIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'

import { Loading, TableMenuButton, ContainedButton, ConfirmDialog } from 'civic-champs-shared'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { ExtendedPagedTable, useFiltersFromQuery } from 'core/table/components'
import DEFAULT_FILTERS, { useGetGlobalFilter } from 'core/table/filters'
import { useConditionalSelectColumn, useGetColumnState } from 'core/table/table-hooks'
import PageHeader from 'App/components/PageHeader'
import CUSTOM_FILTERS from 'champion/utils/filters'
import { getOperatorOptions, useColumnStyles, useSuperEventColumns } from './hooks/useSuperEventColumns'
import { useGetSuperEventLink, SuperEvent } from './types'
import useSuperEventCollection, { SuccessResult } from './hooks/useSuperEventCollection'
import { useSuccessNotification } from 'civic-champs-shared/api/hooks'
import useGetSuperEvent from './hooks/useGetSuperEvent'
import { OpportunityApiResponse } from 'Event/interfaces'

const tableName = 'superEvents'
const filterTypes = { ...DEFAULT_FILTERS, ...CUSTOM_FILTERS }

const useHandleBulkDelete = (
  table: TableInstance<SuperEvent>,
  bulkDelete: (superEvents: SuperEvent[]) => Promise<SuccessResult>,
) => {
  const showPrompt = useShowPrompt(ConfirmDialog)

  return async () => {
    const confirmed = await showPrompt({
      text:
        'Deleting a Super Event will remove its group list page.  ' +
        'Individual events and registrations will remain unchanged.',
      title: 'Are you sure you want to delete selected Super Events?',
      confirmText: 'Delete Selected Super Events',
      cancelText: 'Do Not Delete',
    })

    if (!confirmed) return

    const rowSelector = table as unknown as UseRowSelectInstanceProps<SuperEvent>
    const superEvents = map(rowSelector.selectedFlatRows, row => row.original)

    if (isEmpty(superEvents)) return

    const { success } = await bulkDelete(superEvents)
    if (success) rowSelector.toggleAllRowsSelected(false)
  }
}

const useOnEdit = (
  edit: (superEvent: SuperEvent) => Promise<SuccessResult>,
): [(toEdit: SuperEvent) => Promise<void>, boolean] => {
  const [getSuperEvent, { loading }] = useGetSuperEvent()

  const onEdit = useCallback(
    async ({ id }: SuperEvent) => {
      const superEvent = await getSuperEvent(id as number)

      //The api returns full occurrences, we want to map them to SuperEventMember[]
      superEvent.events = map(superEvent.events as OpportunityApiResponse[], event => ({
        opportunityId: event.opportunityId,
        date: moment.tz(event.startsAt, event.organization.timeZone).format('YYYY-MM-DD'),
      }))

      edit(superEvent)
    },
    [getSuperEvent, edit],
  )

  return [onEdit, loading]
}

const useOnCopy = () => {
  const getSuperEventLink = useGetSuperEventLink()
  const showSuccess = useSuccessNotification()

  return useCallback(
    async (toCopy: SuperEvent) => {
      const url = getSuperEventLink(toCopy)
      await navigator.clipboard.writeText(url)
      showSuccess('Link copied')
    },
    [getSuperEventLink, showSuccess],
  )
}

const SuperEventsComponent = (props: RouteComponentProps) => {
  const { location, history } = props

  const { filters } = useFiltersFromQuery(location.search, getOperatorOptions)

  const [{ superEvents, loading, busy }, { add, edit, bulkDelete }] = useSuperEventCollection()

  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  useEffectOnce(() => {
    fetchColumnState(tableName)
  })

  const [onEdit, editLoading] = useOnEdit(edit)
  const onCopy = useOnCopy()

  const columnStyles = useColumnStyles()
  const columns = useSuperEventColumns({ onEdit, onCopy })
  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes,
    getOperatorOptions,
  })

  const table = useTable<SuperEvent>(
    {
      initialState: {
        hiddenColumns: ['startsAt', 'endsAt'],
        // @ts-ignore
        globalFilter: '',
        filters,
      },
      globalFilter,
      filterTypes,
      data: superEvents,
      columns,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const selectedRows = (table as unknown as UseRowSelectInstanceProps<SuperEvent>).selectedFlatRows
  const selectedRowCount = size(selectedRows)

  const handleBulkDelete = useHandleBulkDelete(table, bulkDelete)

  if (loading || columnStateLoading) return <Loading />

  return (
    <ExtendedPagedTable
      history={history}
      location={location}
      table={table}
      columns={columns}
      columnState={columnState}
      tableName={tableName}
      compactView
      expandable={false}
      useGlobalSearch
      useFilters
      addButton={
        <ContainedButton disabled={busy || editLoading} onClick={add} startIcon={<AddIcon />}>
          Add Super Event
        </ContainedButton>
      }
      buttons={
        <TableMenuButton
          startIcon={<CancelIcon />}
          disabled={!selectedRowCount || busy || editLoading}
          onClick={handleBulkDelete}
          rightMargin
        >
          Cancel
        </TableMenuButton>
      }
    />
  )
}

export const SuperEvents = (props: RouteComponentProps) => (
  <PageHeader title="Super Events" subTitle="Create and manage your grouped events">
    <SuperEventsComponent {...props} />
  </PageHeader>
)
